import React,{useState} from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const {FiArrowLeft,FiCheckCircle,FiShield,FiTrendingUp,FiHome,FiDollarSign,FiAlertTriangle,FiCheck,FiInfo,FiThermometer,FiLayers,FiMapPin,FiHelpCircle,FiX,FiActivity,FiMinusCircle}=FiIcons;

// Interaktiver Rechner Komponente
const AmortisationCalculator=()=> {
  const [area,setArea]=useState(60);
  const [costPerSqm,setCostPerSqm]=useState(35);// Standardwert aus Text (DIY)
  const [savingPerSqm,setSavingPerSqm]=useState(5);// Standardwert aus Text

  const totalInvest=area * costPerSqm;
  const yearlySaving=area * savingPerSqm;
  const amortizationYears=yearlySaving > 0 ? (totalInvest / yearlySaving).toFixed(1) : 0;

  return (
    <div className="bg-white border-2 border-mk-teal-100 rounded-2xl p-6 md:p-8 my-10 shadow-lg">
      <h3 className="text-xl font-bold text-mk-teal-900 mb-6 flex items-center">
        <SafeIcon icon={FiDollarSign} className="mr-2" />
        Lohnt es sich? Ihr persönlicher Schnell-Check
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        {/* Eingabebereich */}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2 flex justify-between">
              <span>Kellerfläche</span>
              <span className="text-mk-teal-600">{area} m²</span>
            </label>
            <input type="range" min="10" max="150" step="5" value={area} onChange={(e)=> setArea(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-mk-teal-500" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2 flex justify-between">
              <span>Kosten pro m² (Material/Handwerker)</span>
              <span className="text-mk-teal-600">{costPerSqm} €</span>
            </label>
            <input type="range" min="10" max="120" step="5" value={costPerSqm} onChange={(e)=> setCostPerSqm(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-mk-teal-500" />
            <p className="text-xs text-gray-500 mt-1">
              *Tipp: DIY ca. 30-40 €,Fachfirma ca. 80-120 €
            </p>
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2 flex justify-between">
              <span>Geschätzte Einsparung pro m²/Jahr</span>
              <span className="text-mk-teal-600">{savingPerSqm} €</span>
            </label>
            <input type="range" min="2" max="15" step="0.5" value={savingPerSqm} onChange={(e)=> setSavingPerSqm(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-mk-teal-500" />
            <p className="text-xs text-gray-500 mt-1">
              *Realistisch sind oft 4-8 € je nach Energiepreis & Dämmstärke
            </p>
          </div>
        </div>

        {/* Ergebnisbereich */}
        <div className="bg-mk-teal-50 rounded-xl p-6 flex flex-col justify-center space-y-6">
          <div className="flex justify-between items-end border-b border-mk-teal-200 pb-2">
            <span className="text-gray-600 text-sm">Investitionssumme:</span>
            <span className="text-xl font-bold text-gray-900">{totalInvest.toLocaleString()} €</span>
          </div>
          <div className="flex justify-between items-end border-b border-mk-teal-200 pb-2">
            <span className="text-gray-600 text-sm">Jährliche Ersparnis:</span>
            <span className="text-xl font-bold text-green-600">{yearlySaving.toLocaleString()} €</span>
          </div>
          <div className="pt-2">
            <span className="block text-gray-600 text-sm mb-1">Amortisation nach:</span>
            <div className="flex items-center">
              <span className="text-4xl font-bold text-mk-teal-700">{amortizationYears}</span>
              <span className="text-lg text-mk-teal-600 ml-2 font-medium">Jahren</span>
            </div>
            <p className="text-xs text-gray-500 mt-2 italic">
              *Vereinfachte Rechnung ohne Energiepreissteigerung/Zinsen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

function Kellerdeckendaemmung() {
  const articleSchema={"@context": "https://schema.org","@type": "BlogPosting","headline": "Kellerdeckendämmung – sinnvoll oder überschätzt?","description": "Kellerdeckendämmung richtig einordnen: Wann sie sinnvoll ist,welche Einsparungen realistisch sind und warum Fassaden und Fenster oft wichtiger sind. Fokus NRW und Kreis Heinsberg.","author": {"@type": "Organization","name": "MK-Ausbau","url": "https://ausbau.nrw"},"publisher": {"@type": "Organization","name": "MK-Ausbau","logo": {"@type": "ImageObject","url": "https://ausbau.nrw/logo-icon.svg"}},"datePublished": "2026-04-25","image": "https://mx.ausbau.nrw/img/Daemmtechnik_xl.jpg","mainEntityOfPage": {"@type": "WebPage","@id": "https://ausbau.nrw/blog/kellerdeckendaemmung"}};
  const faqSchema={"@context": "https://schema.org","@type": "FAQPage","mainEntity": [ {"@type": "Question","name": "Wann ist Kellerdeckendämmung sinnvoll?","acceptedAnswer": {"@type": "Answer","text": "Wenn der Keller unbeheizt ist und eine deutliche Temperaturdifferenz zum Wohnraum besteht. Bei beheizten Kellern ist sie meist wirkungslos."}},{"@type": "Question","name": "Warum bringt Kellerdeckendämmung weniger als Fassadendämmung?","acceptedAnswer": {"@type": "Answer","text": "Weil die Fläche kleiner ist,der Temperaturunterschied geringer und der Wärmestrom nach unten bauphysikalisch begrenzt ist."}},{"@type": "Question","name": "Verursacht Kellerdeckendämmung kalte Keller?","acceptedAnswer": {"@type": "Answer","text": "Ja,der Keller wird kälter,da weniger Wärme aus dem Wohnraum eintritt. Das ist normal und kein Schaden."}},{"@type": "Question","name": "Ist Kellerdeckendämmung für Förderungen relevant?","acceptedAnswer": {"@type": "Answer","text": "In bestimmten Effizienzhaus-Förderprogrammen kann sie erforderlich sein,auch wenn der energetische Einzelbeitrag gering ist."}} ]};
  const fadeIn={initial: {opacity: 0,y: 20},animate: {opacity: 1,y: 0},transition: {duration: 0.6}};

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Kellerdeckendämmung: Sinnvoll oder Geldverschwendung? – Check" 
        description="Lohnt sich Dämmung im Keller? 📉 Realistischer Check: Einsparung vs. Kosten. Wann es kalte Füße löst und wann nicht. Mit Rechner!" 
        keywords="Kellerdeckendämmung sinnvoll,Dämmung Kellerdecke Kosten,kalte Füße Altbau,Heizkosten sparen Keller,Effizienzhaus NRW" 
        schema={[articleSchema,faqSchema]} 
        canonical="/blog/kellerdeckendaemmung" 
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
        <Link to="/blog" className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group" >
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>
        <motion.article {...fadeIn} className="prose prose-lg prose-mk max-w-none">
          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4 text-left">
              <SafeIcon icon={FiThermometer} />
              <span>Energie & Dämmung</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight mb-6 text-left">
              Kellerdeckendämmung – sinnvoll oder überschätzt?
            </h1>
            <p className="text-xl font-medium text-mk-teal-700 mb-6">
              Wann sie wirklich lohnt und wann andere Maßnahmen mehr bringen
            </p>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiShield} className="mr-1" /> Experten-Check</span>
              <span className="flex items-center"><SafeIcon icon={FiInfo} className="mr-1" /> Fokus: NRW / Heinsberg</span>
            </div>
          </header>

          {/* ... Content unchanged ... */}
          {/* Intro */}
          <div className="lead text-xl text-gray-600 leading-relaxed mb-12 text-left">
            Die Kellerdeckendämmung gilt oft als schneller Einstieg in die energetische Sanierung. Doch aus bauphysikalischer Sicht gilt: <strong>Sie ist sinnvoll – aber nicht immer die wirksamste Maßnahme.</strong>
          </div>
          <p className="text-gray-700">
            Gerade bei Einfamilienhäusern im Kreis Heinsberg,am Niederrhein und im Raum Mönchengladbach,Viersen,Neuss und Düsseldorf ist eine realistische Einordnung entscheidend,um Budget sinnvoll einzusetzen.
          </p>
          <p className="text-gray-700 mb-8 font-medium">
            MK-Ausbau aus Heinsberg setzt deshalb auf eine klare Regel:<br/>
            👉 <strong>Nicht jede Dämmmaßnahme zuerst – sondern die mit dem größten Effekt.</strong>
          </p>

          {/* Wann Teil der Hülle */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiHome} className="mr-3 text-mk-teal-600" />
            Wann ist eine Kellerdeckendämmung überhaupt Teil der Gebäudehülle?
          </h2>
          <div className="bg-mk-teal-50 p-6 rounded-xl border-l-4 border-mk-teal-500 mb-8">
            <p className="font-bold text-mk-teal-900 mb-2 flex items-center"><SafeIcon icon={FiAlertTriangle} className="mr-2"/> Entscheidender Punkt:</p>
            <p className="text-mk-teal-800 m-0">Die Kellerdecke gehört nur dann zur thermischen Hülle,wenn der Keller <strong>unbeheizt</strong> ist.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="flex items-start bg-blue-50 p-4 rounded-lg">
              <SafeIcon icon={FiCheckCircle} className="text-blue-500 mr-3 mt-1 flex-shrink-0" />
              <div>
                <strong className="block text-blue-900 mb-1">Unbeheizter Keller</strong>
                <span className="text-sm text-blue-800">Kellerdecke=Trennfläche warm/kalt → Dämmung sinnvoll</span>
              </div>
            </div>
            <div className="flex items-start bg-gray-50 p-4 rounded-lg">
              <SafeIcon icon={FiMinusCircle} className="text-gray-500 mr-3 mt-1 flex-shrink-0" />
              <div>
                <strong className="block text-gray-900 mb-1">Beheizter Keller</strong>
                <span className="text-sm text-gray-700">Hülle verlagert sich auf Kellerwände & Boden → Deckendämmung bringt kaum Nutzen</span>
              </div>
            </div>
          </div>
          <p className="text-gray-700">
            Das ist ein häufiger Planungsfehler – und einer der Gründe,warum MK-Ausbau immer zuerst die Nutzung des Kellers prüft.
          </p>

          {/* Wärmeverluste */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Wie groß sind die Wärmeverluste wirklich?</h2>
          <p className="text-gray-700 mb-4">Hier hilft ein nüchterner Blick auf die Zahlen:</p>
          <ul className="space-y-3 mb-6">
            <li className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-100">
              <span className="font-medium text-gray-700">Kellerdecke</span>
              <span className="font-bold text-gray-900">ca. 5–10 %</span>
            </li>
            <li className="flex items-center justify-between bg-mk-teal-50 p-3 rounded-lg border border-mk-teal-100">
              <span className="font-medium text-mk-teal-900">Außenwände & Dach</span>
              <span className="font-bold text-mk-teal-700">ca. 20–30 % (je Bauteil)</span>
            </li>
          </ul>
          <p className="text-gray-700 font-bold mb-2">Warum ist der Verlust nach unten geringer?</p>
          <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-6">
            <li>kleinere Fläche als Fassaden</li>
            <li>geringerer Temperaturunterschied (Keller im Winter meist 10–15 °C)</li>
            <li>Wärme fließt nach unten schlechter als nach oben</li>
          </ul>
          <p className="bg-gray-100 p-4 rounded-lg italic text-gray-700">
            👉 <strong>Ergebnis:</strong> Die energetische Wirkung ist real,aber deutlich geringer als bei Dach- oder Fassadendämmung.
          </p>

          {/* Warum Keller warm bleiben */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Warum Keller im Winter überraschend warm bleiben</h2>
          <p className="text-gray-700 mb-4">Viele unterschätzen die „natürliche Wärme“ des Kellers:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center"><SafeIcon icon={FiMapPin} className="mr-2 text-mk-teal-500"/> Erdreich als Puffer</h4>
              <p className="text-sm text-gray-600">In ~2 m Tiefe liegt die Bodentemperatur auch im Winter oft bei ≈ 12 °C. Der Keller friert von unten nicht ein.</p>
            </div>
            <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center"><SafeIcon icon={FiActivity} className="mr-2 text-mk-red-500"/> Interne Quellen</h4>
              <p className="text-sm text-gray-600">Abwärme von Heizkessel,Warmwasserspeicher,Rohrleitungen und Standby-Geräten heizt passiv mit.</p>
            </div>
          </div>

          {/* Bauphysik Beispiel */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiLayers} className="mr-3 text-mk-teal-600" />
            Beispiel aus der Bauphysik: U-Werte & Temperaturen
          </h2>
          <div className="bg-gray-900 text-white p-8 rounded-2xl shadow-xl mb-12">
            <h4 className="text-mk-teal-400 font-bold mb-4 border-b border-gray-700 pb-2">Typischer Altbau (1950–1975)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-400 text-xs uppercase font-bold mb-1">Ungedämmt</p>
                <p className="text-2xl font-bold mb-2">U-Wert ≈ 1,30</p>
                <p className="text-sm text-gray-400">Bodentemperatur: ≈ 18,1 °C</p>
              </div>
              <div>
                <p className="text-mk-teal-400 text-xs uppercase font-bold mb-1">+ 8 cm Dämmung</p>
                <p className="text-2xl font-bold mb-2">U-Wert ≈ 0,33</p>
                <p className="text-sm text-gray-300">Bodentemperatur: ≈ 20,4 °C</p>
              </div>
            </div>
            <div className="mt-6 bg-white/10 p-4 rounded-lg">
              <p className="m-0 font-medium text-sm flex items-start">
                <SafeIcon icon={FiInfo} className="mr-2 mt-1"/>
                <strong>Der Realitäts-Check:</strong> Der Temperaturunterschied von ca. 2,3 Grad ist messbar,aber oft nicht der alleinige Löser für "kalte Füße".
              </p>
            </div>
          </div>

          {/* Wahre Ursache kalte Füße */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Die wahre Ursache für kalte Füße in Altbauten</h2>
          <p className="text-gray-700 mb-6">Oft liegt das Problem gar nicht am Boden selbst,sondern an der Luftzirkulation:</p>
          <ul className="space-y-4 mb-8">
            <li className="bg-gray-50 p-4 rounded-lg">
              <strong className="block text-gray-900 mb-1">1. Kaltluftsee durch Fenster & Wände</strong>
              <span className="text-sm text-gray-700">Kalte Luft fällt an schlecht gedämmten Fenstern und Außenwänden nach unten und sammelt sich am Boden. Das passiert auch im 1. Stock ohne Keller!</span>
            </li>
            <li className="bg-gray-50 p-4 rounded-lg">
              <strong className="block text-gray-900 mb-1">2. Bodenbelag-Physik</strong>
              <span className="text-sm text-gray-700">Fliesen entziehen dem Fuß Wärme viel schneller als Holz oder Teppich (hohe thermische Effusivität). Dämmung unter der Decke ändert das Materialgefühl oben nicht.</span>
            </li>
          </ul>

          {/* Entscheidungsregel */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Entscheidungsregel: Wann lohnt es sich?</h2>
          <div className="space-y-4 mb-12">
            <div className="flex items-center p-4 bg-red-50 border border-red-100 rounded-xl">
              <SafeIcon icon={FiX} className="text-red-500 w-6 h-6 mr-3" />
              <span className="text-red-900 font-medium">Fassade schlecht gedämmt + Keller relativ warm → <strong>Geringer Nutzen</strong></span>
            </div>
            <div className="flex items-center p-4 bg-green-50 border border-green-100 rounded-xl">
              <SafeIcon icon={FiCheck} className="text-green-500 w-6 h-6 mr-3" />
              <span className="text-green-900 font-medium">Temperaturdifferenz Wohnraum ↔ Keller ≥ 10 °C → <strong>Dämmung sinnvoll</strong></span>
            </div>
          </div>

          {/* INTERAKTIVER RECHNER */}
          <div id="rechner">
            <AmortisationCalculator />
          </div>

          {/* Kosten & Nebenwirkungen */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Kosten,Einsparungen & Nebenwirkungen</h2>
          <p className="text-gray-700 mb-6">
            Wie im Rechner oben zu sehen,ist die Amortisation bei Eigenleistung oft attraktiv (unter 10 Jahre).
          </p>
          <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-xl mb-8">
            <h4 className="font-bold text-orange-900 mb-2 flex items-center"><SafeIcon icon={FiAlertTriangle} className="mr-2"/> Wichtige Nebenwirkung:</h4>
            <p className="text-orange-800 text-sm m-0">
              Der Keller wird kälter! Da weniger Wärme von oben "verloren" geht,sinkt die Kellertemperatur. <br/>
              <strong>Vorteil:</strong> Bessere Lagerbedingungen für Vorräte. <br/>
              <strong>Nachteil:</strong> Feuchtigkeit kann an kalten Kellerwänden eher kondensieren (Lüften wichtig!).
            </p>
          </div>

          {/* DIY Überblick */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiLayers} className="mr-3 text-mk-teal-600" />
            DIY-Kurzüberblick (Orientierung)
          </h2>
          <p className="text-gray-700 mb-4">Häufigste Lösung: EPS-Platten oder Mineralwolle direkt unter die Decke kleben/dübeln.</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 text-sm mb-8">
            <li>Untergrund muss sauber & tragfähig sein</li>
            <li>Wärmebrücken an Wandanschlüssen beachten</li>
            <li>Ggf. "Flankendämmung" (ca. 50 cm an der Kellerwand runter dämmen)</li>
          </ul>
          <p className="text-xs text-gray-500 italic">Hinweis: MK-Ausbau übernimmt keine DIY-Anleitungen,bewertet aber gerne,ob sich Eigenleistung für Sie lohnt.</p>

          {/* Kombination & Förderung */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Sonderfall: Effizienzhaus & Förderlogik</h2>
          <p className="text-gray-700 mb-6">
            Auch wenn die Maßnahme einzeln betrachtet "wenig" bringt: Für KfW-Effizienzhäuser müssen oft <strong>alle</strong> Hüllflächen gedämmt sein,um den Standard rechnerisch zu erreichen. Hier wird die Kellerdeckendämmung zur Pflichtübung für die Förderung.
          </p>

          {/* Einsatzgebiet */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiMapPin} className="mr-3 text-mk-teal-600" />
            Einsatzgebiet in NRW
          </h2>
          <p className="text-gray-700 mb-6">MK-Ausbau prüft und saniert regelmäßig in:</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-12">
            <span>• Heinsberg</span>
            <span>• Erkelenz</span>
            <span>• Hückelhoven</span>
            <span>• Wegberg</span>
            <span>• Geilenkirchen</span>
            <span>• Übach-Palenberg</span>
            <span>• Mönchengladbach</span>
            <span>• Düsseldorf</span>
          </div>

          {/* Fazit */}
          <div className="bg-mk-teal-600 text-white p-8 rounded-2xl shadow-xl mt-16 mb-12">
            <h2 className="text-white text-2xl font-bold mb-4 mt-0">Fazit: Realistisch planen</h2>
            <ul className="space-y-3 list-none pl-0 mb-6">
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1"/> Nur bei unbeheiztem Keller wirklich effektiv</li>
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1"/> Energetisch meist Platz 3 nach Dach & Fassade</li>
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1"/> Keine Wunderlösung für "kalte Füße" (oft liegt's am Fenster)</li>
            </ul>
            <p className="text-white font-bold border-t border-mk-teal-400 pt-4">
              👉 Gute Planung spart mehr Geld als jede blind geklebte Dämmplatte.
            </p>
          </div>

          {/* FAQ */}
          <div className="mt-20 pt-12 border-t border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              FAQ – Kellerdeckendämmung
            </h2>
            <div className="space-y-4">
              {[
                {q: "Wann ist Kellerdeckendämmung sinnvoll?",a: "Wenn der Keller unbeheizt ist und die Temperaturdifferenz zum Wohnraum mindestens etwa 10 °C beträgt."},
                {q: "Löst Kellerdeckendämmung kalte Füße?",a: "In der Regel nein. Kalte Füße entstehen meist durch kalte Fassaden,Fenster (Kaltluftabfall) und die Art des Bodenbelags."},
                {q: "Wie hoch sind die Einsparungen?",a: "Typisch sind 5–10 % der Hüllenverluste – deutlich weniger als bei Dach oder Außenwand."},
                {q: "Wird der Keller kälter?",a: "Ja. Das ist normal und bauphysikalisch logisch,da weniger Wärme von oben nachkommt."},
                {q: "Ist Kellerdeckendämmung Pflicht für Förderungen?",a: "In manchen Effizienzhaus-Programmen ja,um den rechnerischen U-Wert des Gesamtgebäudes zu erreichen."}
              ].map((faq,index)=> (
                <div key={index} className="bg-gray-50 rounded-xl p-6 hover:bg-mk-teal-50 transition-colors cursor-default">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start">
                    <SafeIcon icon={FiHelpCircle} className="text-mk-teal-500 mt-1 mr-3 flex-shrink-0" />
                    {faq.q}
                  </h3>
                  <p className="text-gray-700 text-sm ml-8 leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center py-12 mt-12 border-t border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Unsicher,wo Sie anfangen sollen?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Wir prüfen Ihr Haus ganzheitlich. Lohnt sich der Keller? Oder doch lieber das Dach? Wir finden die wirtschaftlichste Lösung für Sie.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt" className="bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center" >
                Sanierungs-Check anfragen
              </Link>
              <Link to="/daemmtechnik" className="bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center" >
                Mehr zu Dämmtechnik
              </Link>
            </div>
          </div>
        </motion.article>
      </div>
      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default Kellerdeckendaemmung;